<template>
  <v-card class="orders-card" elevation="4">
    <v-card-title>
      <h2 class="section-title">Bestellungen</h2>
    </v-card-title>
    <v-card-text>
      <v-row class="order-stats">
        <v-col cols="6" v-for="(stat, index) in orderStats" :key="index">
          <v-hover v-slot:default="{ hover }">
            <v-card
              :elevation="hover ? 8 : 0"
              :class="['order-stat-card', hover ? 'hovered' : '']"
              :color="stat.color"
            >
              <v-card-text class="pa-3">
                <div class="order-stat-value">{{ stat.value }}</div>
                <div class="order-stat-label">{{ stat.label }}</div>
                <v-icon class="order-stat-icon">{{ stat.icon }}</v-icon>
              </v-card-text>
            </v-card>
          </v-hover>
        </v-col>
      </v-row>
      
      <!-- Bestellungsbereiche -->
      <div class="mt-4">
        <h3 class="metric-title">Bestellungen nach Marktplatz</h3>
        <v-list dense dark class="marketplace-list pa-0" rounded>
          <v-list-item
            v-for="(value, key) in orders.openOrders"
            :key="key"
            class="marketplace-item"
          >
            <v-list-item-avatar>
              <v-avatar size="32" :color="getMarketplaceColor(key)">
                <span class="white--text text-uppercase">{{ key.charAt(0) }}</span>
              </v-avatar>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ key }}</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action>
              <v-chip color="primary" class="marketplace-count">{{ value }}</v-chip>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </div>

      <!-- Lyto International Orders -->
      <div class="mt-4">
        <h3 class="metric-title">Lyto International (AT/DE/FR/CH)</h3>

        <!-- Before Valentine's Day Cutoffs -->
        <div class="date-section-header mt-2">
          <v-icon small color="orange">mdi-clock-alert-outline</v-icon>
          <span class="ml-1">Vor Valentinstag-Cutoff (CH: 6. Feb, FR: 7. Feb, AT: 10. Feb, DE: 11. Feb)</span>
        </div>
        <v-list dense dark class="lyto-list pa-0" rounded>
          <!-- Switzerland Before -->
          <v-list-item class="lyto-item">
            <v-list-item-avatar>
              <v-avatar size="32" color="red">
                <span class="white--text">🇨🇭</span>
              </v-avatar>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>Schweiz</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action class="flex-row">
              <v-chip color="purple" small class="mr-2">
                <v-icon small left>mdi-cart-multiple</v-icon>
                {{ orders.lytoMultiCHBefore || 0 }}
              </v-chip>
              <v-chip color="blue" small>
                <v-icon small left>mdi-cart</v-icon>
                {{ orders.lytoSingleCHBefore || 0 }}
              </v-chip>
            </v-list-item-action>
          </v-list-item>

          <!-- France Before -->
          <v-list-item class="lyto-item">
            <v-list-item-avatar>
              <v-avatar size="32" color="blue">
                <span class="white--text">🇫🇷</span>
              </v-avatar>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>Frankreich</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action class="flex-row">
              <v-chip color="purple" small class="mr-2">
                <v-icon small left>mdi-cart-multiple</v-icon>
                {{ orders.lytoMultiFRBefore || 0 }}
              </v-chip>
              <v-chip color="blue" small>
                <v-icon small left>mdi-cart</v-icon>
                {{ orders.lytoSingleFRBefore || 0 }}
              </v-chip>
            </v-list-item-action>
          </v-list-item>

          <!-- Austria Before -->
          <v-list-item class="lyto-item">
            <v-list-item-avatar>
              <v-avatar size="32" color="red">
                <span class="white--text">🇦🇹</span>
              </v-avatar>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>Österreich</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action class="flex-row">
              <v-chip color="purple" small class="mr-2">
                <v-icon small left>mdi-cart-multiple</v-icon>
                {{ orders.lytoMultiATBefore || 0 }}
              </v-chip>
              <v-chip color="blue" small>
                <v-icon small left>mdi-cart</v-icon>
                {{ orders.lytoSingleATBefore || 0 }}
              </v-chip>
            </v-list-item-action>
          </v-list-item>

          <!-- Germany Before -->
          <v-list-item class="lyto-item">
            <v-list-item-avatar>
              <v-avatar size="32" color="black">
                <span class="white--text">🇩🇪</span>
              </v-avatar>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>Deutschland</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action class="flex-row">
              <v-chip color="purple" small class="mr-2">
                <v-icon small left>mdi-cart-multiple</v-icon>
                {{ orders.lytoMultiDEBefore || 0 }}
              </v-chip>
              <v-chip color="blue" small>
                <v-icon small left>mdi-cart</v-icon>
                {{ orders.lytoSingleDEBefore || 0 }}
              </v-chip>
            </v-list-item-action>
          </v-list-item>
        </v-list>

        <!-- After Valentine's Day Cutoffs -->
        <div class="date-section-header mt-3">
          <v-icon small color="green">mdi-clock-check-outline</v-icon>
          <span class="ml-1">Nach Valentinstag-Cutoff (CH: 9. Feb, FR/AT: 10. Feb, DE: 11. Feb)</span>
        </div>
        <v-list dense dark class="lyto-list pa-0" rounded>
          <!-- Switzerland After -->
          <v-list-item class="lyto-item">
            <v-list-item-avatar>
              <v-avatar size="32" color="red">
                <span class="white--text">🇨🇭</span>
              </v-avatar>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>Schweiz</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action class="flex-row">
              <v-chip color="purple" small class="mr-2">
                <v-icon small left>mdi-cart-multiple</v-icon>
                {{ orders.lytoMultiCHAfter || 0 }}
              </v-chip>
              <v-chip color="blue" small>
                <v-icon small left>mdi-cart</v-icon>
                {{ orders.lytoSingleCHAfter || 0 }}
              </v-chip>
            </v-list-item-action>
          </v-list-item>

          <!-- France After -->
          <v-list-item class="lyto-item">
            <v-list-item-avatar>
              <v-avatar size="32" color="blue">
                <span class="white--text">🇫🇷</span>
              </v-avatar>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>Frankreich</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action class="flex-row">
              <v-chip color="purple" small class="mr-2">
                <v-icon small left>mdi-cart-multiple</v-icon>
                {{ orders.lytoMultiFRAfter || 0 }}
              </v-chip>
              <v-chip color="blue" small>
                <v-icon small left>mdi-cart</v-icon>
                {{ orders.lytoSingleFRAfter || 0 }}
              </v-chip>
            </v-list-item-action>
          </v-list-item>

          <!-- Austria After -->
          <v-list-item class="lyto-item">
            <v-list-item-avatar>
              <v-avatar size="32" color="red">
                <span class="white--text">🇦🇹</span>
              </v-avatar>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>Österreich</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action class="flex-row">
              <v-chip color="purple" small class="mr-2">
                <v-icon small left>mdi-cart-multiple</v-icon>
                {{ orders.lytoMultiATAfter || 0 }}
              </v-chip>
              <v-chip color="blue" small>
                <v-icon small left>mdi-cart</v-icon>
                {{ orders.lytoSingleATAfter || 0 }}
              </v-chip>
            </v-list-item-action>
          </v-list-item>

          <!-- Germany After -->
          <v-list-item class="lyto-item">
            <v-list-item-avatar>
              <v-avatar size="32" color="black">
                <span class="white--text">🇩🇪</span>
              </v-avatar>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>Deutschland</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action class="flex-row">
              <v-chip color="purple" small class="mr-2">
                <v-icon small left>mdi-cart-multiple</v-icon>
                {{ orders.lytoMultiDEAfter || 0 }}
              </v-chip>
              <v-chip color="blue" small>
                <v-icon small left>mdi-cart</v-icon>
                {{ orders.lytoSingleDEAfter || 0 }}
              </v-chip>
            </v-list-item-action>
          </v-list-item>
        </v-list>

        <!-- Legend -->
        <div class="mt-2 d-flex justify-center">
          <v-chip x-small color="purple" class="mr-2">
            <v-icon x-small left>mdi-cart-multiple</v-icon>
            Multicart
          </v-chip>
          <v-chip x-small color="blue">
            <v-icon x-small left>mdi-cart</v-icon>
            Singlecart
          </v-chip>
        </div>
      </div>

      <!-- Muckelmeister Orders -->
      <div class="mt-4">
        <h3 class="metric-title">Muckelmeister</h3>

        <!-- Before Feb 11th Section -->
        <div class="date-section-header mt-2">
          <v-icon small color="orange">mdi-clock-alert-outline</v-icon>
          <span class="ml-1">Vor 11. Feb 23:59</span>
        </div>
        <v-list dense dark class="lyto-list pa-0" rounded>
          <v-list-item class="lyto-item">
            <v-list-item-content>
              <v-list-item-title>Bestellungen</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action class="flex-row">
              <v-chip color="purple" small class="mr-2">
                <v-icon small left>mdi-cart-multiple</v-icon>
                {{ orders.muckelmeisterMultiBefore || 0 }}
              </v-chip>
              <v-chip color="blue" small>
                <v-icon small left>mdi-cart</v-icon>
                {{ orders.muckelmeisterSingleBefore || 0 }}
              </v-chip>
            </v-list-item-action>
          </v-list-item>
        </v-list>

        <!-- After Feb 11th Section -->
        <div class="date-section-header mt-3">
          <v-icon small color="green">mdi-clock-check-outline</v-icon>
          <span class="ml-1">Nach 11. Feb 23:59</span>
        </div>
        <v-list dense dark class="lyto-list pa-0" rounded>
          <v-list-item class="lyto-item">
            <v-list-item-content>
              <v-list-item-title>Bestellungen</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action class="flex-row">
              <v-chip color="purple" small class="mr-2">
                <v-icon small left>mdi-cart-multiple</v-icon>
                {{ orders.muckelmeisterMultiAfter || 0 }}
              </v-chip>
              <v-chip color="blue" small>
                <v-icon small left>mdi-cart</v-icon>
                {{ orders.muckelmeisterSingleAfter || 0 }}
              </v-chip>
            </v-list-item-action>
          </v-list-item>
        </v-list>

        <div class="mt-2 d-flex justify-center">
          <v-chip x-small color="purple" class="mr-2">
            <v-icon x-small left>mdi-cart-multiple</v-icon>
            Multicart
          </v-chip>
          <v-chip x-small color="blue">
            <v-icon x-small left>mdi-cart</v-icon>
            Singlecart
          </v-chip>
        </div>
      </div>

      <!-- Bestellungsstatistik als Balkendiagramm -->
      <div class="mt-4 order-bar-chart-container">
        <h3 class="metric-title mb-3">Bestellungsstatistik</h3>
        <apexchart
          v-if="barChartSeries.length > 0"
          type="bar"
          height="200"
          :options="barChartOptions"
          :series="barChartSeries"
        ></apexchart>
      </div>
      
      <!-- Bestellungschart -->
      <div class="mt-4">
        <h3 class="metric-title mb-2">Bestellungsübersicht</h3>
        <apexchart
          v-if="series.length > 0"
          type="donut"
          height="250"
          :options="chartOptions"
          :series="series"
        ></apexchart>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';

export default {
  name: 'OrdersCard',
  
  components: {
    apexchart: VueApexCharts,
  },
  
  props: {
    orders: {
      type: Object,
      required: true
    }
  },
  
  data() {
    return {
      chartOptions: {
        chart: {
          type: 'donut',
          background: 'transparent'
        },
        labels: [],
        theme: {
          mode: 'dark'
        },
        colors: ['#00E396', '#FEB019', '#FF4560', '#775DD0', '#00D9FF'],
        legend: {
          position: 'bottom',
          labels: {
            colors: '#fff'
          }
        },
        dataLabels: {
          enabled: true,
          style: {
            colors: ['#fff']
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '65%'
            }
          }
        }
      },
      series: [],
      
      barChartOptions: {
        chart: {
          type: 'bar',
          background: 'transparent',
          toolbar: { show: false }
        },
        theme: {
          mode: 'dark'
        },
        xaxis: {
          categories: ['Offen', 'Nicht verpackt', 'Abgeschlossen'],
          labels: {
            style: { colors: '#fff' }
          }
        },
        yaxis: {
          labels: {
            style: { colors: '#fff' }
          }
        },
        colors: ['#00E396', '#FEB019', '#FF4560'],
        dataLabels: {
          enabled: false
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            horizontal: false,
          }
        },
        grid: {
          borderColor: 'rgba(255, 255, 255, 0.1)'
        }
      },
      barChartSeries: []
    };
  },
  
  computed: {
    orderStats() {
      if (!this.orders) return [];

      return [
        {
          label: 'Offene Bestellungen',
          value: this.orders.openOrdersTotal || 0,
          icon: 'mdi-package-variant',
          color: 'rgba(0, 227, 150, 0.1)'
        },
        {
          label: 'Nicht verpackt',
          value: this.orders.unpackedOrders || 0,
          icon: 'mdi-package-variant-closed',
          color: 'rgba(254, 176, 25, 0.1)'
        },
        {
          label: 'Heute gepackt',
          value: this.orders.packedToday || 0,
          icon: 'mdi-package-check',
          color: 'rgba(0, 200, 100, 0.1)'
        },
        {
          label: 'Abgeschlossen',
          value: this.orders.finishedOrders || 0,
          icon: 'mdi-check-circle',
          color: 'rgba(255, 69, 96, 0.1)'
        }
      ];
    }
  },
  
  watch: {
    orders: {
      immediate: true,
      deep: true,
      handler(newOrders) {
        if (newOrders) {
          this.updateCharts();
        }
      }
    }
  },
  
  methods: {
    updateCharts() {
      // Update donut chart
      if (this.orders.openOrders) {
        const labels = Object.keys(this.orders.openOrders);
        const values = Object.values(this.orders.openOrders);
        
        this.chartOptions = {
          ...this.chartOptions,
          labels: labels
        };
        this.series = values;
      }
      
      // Update bar chart
      this.barChartSeries = [{
        name: 'Bestellungen',
        data: [
          this.orders.openOrdersTotal || 0,
          this.orders.unpackedOrders || 0,
          this.orders.finishedOrders || 0
        ]
      }];
    },
    
    getMarketplaceColor(marketplace) {
      const colors = {
        'amazon': 'orange',
        'shopify': 'green',
        'acs': 'blue',
        'muckelmeister': 'red',
        'prime': 'purple',
        'etsy': 'orange',
      };
      return colors[marketplace] || 'grey';
    }
  }
};
</script>

<style scoped>
.orders-card {
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.95) 0%, rgba(20, 20, 20, 0.95) 100%);
}

.section-title {
  font-size: 1.2rem;
  font-weight: 500;
}

.order-stats {
  margin-bottom: 16px;
}

.order-stat-card {
  position: relative;
  transition: all 0.3s ease;
  border-radius: 8px;
  overflow: hidden;
}

.order-stat-card.hovered {
  transform: translateY(-4px);
}

.order-stat-value {
  font-size: 2rem;
  font-weight: bold;
  color: #fff;
}

.order-stat-label {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 4px;
}

.order-stat-icon {
  position: absolute;
  right: 12px;
  top: 12px;
  opacity: 0.3;
  font-size: 2rem;
}

.metric-title {
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.marketplace-list {
  background: rgba(0, 0, 0, 0.2);
}

.marketplace-item {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.marketplace-item:last-child {
  border-bottom: none;
}

.marketplace-count {
  font-weight: bold;
}

.lyto-list {
  background: rgba(0, 0, 0, 0.2);
}

.lyto-item {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.lyto-item:last-child {
  border-bottom: none;
}

.lyto-item .v-list-item__action {
  flex-direction: row;
  gap: 8px;
}

.date-section-header {
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  padding: 4px 8px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  margin-bottom: 8px;
}
</style>